import { useCallback, useEffect, useState } from 'react'
import type { MeDto } from '@praesto/shared'
import { api } from './api.js'
import { navigate, useSegments, usePath } from './router.js'
import { useLanguage, useText } from './i18n.js'
import { LoginPage } from './pages/LoginPage.js'
import { BoardsPage } from './pages/BoardsPage.js'
import { BoardPage } from './pages/BoardPage.js'
import { GuestPage } from './pages/GuestPage.js'
import { RedeemPage } from './pages/RedeemPage.js'
import { DemoPage } from './pages/DemoPage.js'
import { ShowcasePage } from './pages/ShowcasePage.js'
import { SignupPage } from './pages/SignupPage.js'

export function App(): React.ReactElement {
  const segments = useSegments()
  const [user, setUser] = useState<MeDto | null>(null)
  const [checked, setChecked] = useState(false)
  const { setLanguage } = useLanguage()
  const t = useText()

  /**
   * Wer sich anmeldet, bringt seine Sprache mit.
   *
   * Bewusst an *dieser* Stelle und nicht nur beim ersten Laden: Die Anmeldung
   * geschieht mitten in der Sitzung, und ohne das hier bliebe die Oberfläche bis
   * zum nächsten Neuladen in der Sprache des Browsers stehen — auch wenn am
   * Konto etwas anderes hinterlegt ist.
   */
  const signedIn = useCallback(
    (me: MeDto | null) => {
      setUser(me)
      if (me?.language) setLanguage(me.language)
    },
    [setLanguage],
  )

  useEffect(() => {
    api
      .me()
      .then(signedIn)
      .catch(() => setUser(null))
      .finally(() => setChecked(true))
  }, [signedIn])

  // Gast-Routen laufen bewusst vor der Login-Prüfung: ein Gast hat keinen
  // Account und soll auch keinen Login-Screen zu sehen bekommen.
  if (segments[0] === 'g' && segments[1]) {
    return <GuestPage token={segments[1]} />
  }
  // Einmal-Links ebenso: Wer sein Passwort vergessen hat, käme an einem
  // Login-Screen nicht vorbei.
  if (segments[0] === 'zugang' && segments[1]) {
    return <RedeemPage token={segments[1]} onSignedIn={signedIn} />
  }
  /*
   * Die Wege von der Landingpage herein.
   *
   * Die Vorführung läuft vor der Login-Prüfung, weil ihr Besucher Gast ist und
   * bleibt — ein Anmeldebildschirm wäre für ihn eine Sackgasse. `/demo/vortrag`
   * ist dieselbe Demo wie `/demo`, sie beginnt nur sofort im Vortragsmodus.
   */
  if (segments[0] === 'vorfuehrung') return <ShowcasePage />
  if (segments[0] === 'demo') {
    return <DemoPage onSignedIn={signedIn} asTalk={segments[1] === 'vortrag'} />
  }
  /*
   * `/registrieren` legt ein Konto an, `/anmelden` meldet an.
   *
   * Bis die Startseite unter `/` lag, hieß die Kontoanlage `/anmelden` — auf
   * Deutsch heißt das aber „einloggen", und genau dahin zeigt der Knopf oben
   * rechts auf der Startseite. Solange die Anmeldung ohnehin unter `/` lag,
   * fiel das nicht auf; als Startseite hätte es jeden zweiten Besucher in die
   * falsche Maske geführt.
   */
  if (segments[0] === 'registrieren') return <SignupPage />
  if (segments[0] === 'b' && segments[1]) {
    return <BoardPage boardId={segments[1]} />
  }

  if (!checked) {
    return (
      <div className="auth-screen">
        <p className="muted">{t('common.moment')}</p>
      </div>
    )
  }

  return <Workspace user={user} onSignedIn={signedIn} onSignedOut={() => setUser(null)} />
}

/**
 * Der angemeldete Teil: Anmeldemaske oder Board-Übersicht.
 *
 * Beide haben eine eigene Adresse, damit die Zeile im Browser nicht lügt: Bis
 * die Startseite unter `/` lag, hieß beides schlicht `/`, und welches von beidem
 * man sah, hing an der Sitzung. Als Lesezeichen war das unbrauchbar.
 *
 * Berichtigt wird aber **nur zwischen genau diesen beiden Adressen**. Eine
 * frühere Fassung schrieb jede Adresse um, die hier ankam — und weil im
 * Entwicklungsbetrieb auch `/`, `/de/` und jeder Tippfehler hier ankommen
 * (Vite kennt die Routen des Servers nicht und reicht alles an die App weiter),
 * landete man dort ausnahmslos auf `/anmelden` und kam an die Startseite gar
 * nicht mehr heran.
 *
 * Ersetzt statt gepusht: Ein Zurück-Klick soll nicht durch eine Kette von
 * Umleitungen führen.
 */
const SIGNED_OUT = '/anmelden'
const SIGNED_IN = '/boards'

function Workspace({
  user,
  onSignedIn,
  onSignedOut,
}: {
  user: MeDto | null
  onSignedIn: (user: MeDto) => void
  onSignedOut: () => void
}): React.ReactElement {
  const path = usePath()

  useEffect(() => {
    if (user && path === SIGNED_OUT) navigate(SIGNED_IN, true)
    else if (!user && path === SIGNED_IN) navigate(SIGNED_OUT, true)
  }, [path, user])

  if (!user) return <LoginPage onSignedIn={onSignedIn} />
  return <BoardsPage user={user} onSignedOut={onSignedOut} />
}
